import { useMemo } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import { Easing } from 'react-native-reanimated';
import {
    type WithSpringConfig,
    type WithTimingConfig,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
    withTiming,
} from 'react-native-reanimated';

interface UseTapShrinkAnimationOptions {
    onPress?: () => void;
    disabled?: boolean;
    pressScale?: number;
    pressInAnimation?: WithTimingConfig;
    pressOutSpringAnimation?: WithSpringConfig;
}

const DEFAULT_PRESS_SCALE = 0.975;
const TAP_MAX_DURATION_MS = 10_000;
const TAP_MAX_DISTANCE = 24;
const DEFAULT_PRESS_IN_ANIMATION: WithTimingConfig = {
    duration: 300,
    easing: Easing.out(Easing.quad),
};
const DEFAULT_PRESS_OUT_SPRING_ANIMATION: WithSpringConfig = {
    damping: 20,
    stiffness: 220,
    mass: 0.5,
};

/**
 * Provides a tap shrink/expand animation with a configurable press callback.
 * @param options - Hook configuration for scale, timings, and gesture behavior.
 * @returns Tap `gesture` handler, `pressHandlers` for Pressables, and `animatedStyle` to apply to the target view.
 */
export const useTapShrinkAnimation = ({
    onPress,
    disabled = false,
    pressScale = DEFAULT_PRESS_SCALE,
    pressInAnimation = DEFAULT_PRESS_IN_ANIMATION,
    pressOutSpringAnimation = DEFAULT_PRESS_OUT_SPRING_ANIMATION,
}: UseTapShrinkAnimationOptions = {}) => {
    const scale = useSharedValue(1);

    const tapGesture = useMemo(
        () =>
            Gesture.Tap()
                .maxDuration(TAP_MAX_DURATION_MS)
                .maxDistance(TAP_MAX_DISTANCE)
                .enabled(!disabled)
                .onTouchesDown(() => {
                    scale.value = withTiming(pressScale, pressInAnimation);
                })
                .onTouchesUp(() => {
                    scale.value = withSpring(1, pressOutSpringAnimation);
                })
                .onTouchesCancelled(() => {
                    scale.value = withSpring(1, pressOutSpringAnimation);
                })
                .onEnd((_, isSuccess) => {
                    if (isSuccess && onPress) {
                        runOnJS(onPress)();
                    }
                }),
        [scale, disabled, onPress, pressScale, pressInAnimation, pressOutSpringAnimation]
    );

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ scale: scale.value }],
    }));

    const pressHandlers = useMemo(
        () => ({
            onPressIn: () => {
                scale.value = withTiming(pressScale, pressInAnimation);
            },
            onPressOut: () => {
                scale.value = withSpring(1, pressOutSpringAnimation);
            },
        }),
        [scale, pressScale, pressInAnimation, pressOutSpringAnimation]
    );

    return {
        tapGesture,
        animatedStyle,
        pressHandlers,
    };
};
